import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { Commission } from '@/hooks/useCommissions';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';

interface CommissionsTableProps {
  data: Commission[] | undefined;
  isLoading: boolean;
}

export const CommissionsTable = ({ data, isLoading }: CommissionsTableProps) => {
  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (ratio: number) => {
    return `${ratio}%`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'd MMM yyyy', { locale: th });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2 text-muted-foreground">กำลังโหลด...</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        ไม่พบข้อมูลค่าคอมมิชชั่น
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap">รหัสครู</TableHead>
            <TableHead className="whitespace-nowrap">ชื่อครู</TableHead>
            <TableHead className="whitespace-nowrap">รหัสนักเรียน</TableHead>
            <TableHead className="whitespace-nowrap">ชื่อนักเรียน</TableHead>
            <TableHead className="text-center whitespace-nowrap">สัดส่วนการสอน</TableHead>
            <TableHead className="text-center whitespace-nowrap">ชม ที่สอน</TableHead>
            <TableHead className="text-right whitespace-nowrap">ค่าคอร์ส</TableHead>
            <TableHead className="text-right whitespace-nowrap">ค่าคอมมิชชั่น</TableHead>
            <TableHead className="whitespace-nowrap">วันลงทะเบียน</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, idx) => (
            <TableRow key={`${item.teacher_id}-${item.student_id}-${idx}`}>
              <TableCell className="font-mono">{item.teacher_id}</TableCell>
              <TableCell>{item.teacher_name || '-'}</TableCell>
              <TableCell className="font-mono">{item.student_id}</TableCell>
              <TableCell>{item.student_name || '-'}</TableCell>
              <TableCell className="text-center">{formatPercent(item.teaching_ratio)}</TableCell>
              <TableCell className="text-center">
                {item.teacher_specific_sessions} / {item.total_sessions}
              </TableCell>
              <TableCell className="text-right font-mono">{formatNumber(item.course_cost)}</TableCell>
              <TableCell className="text-right font-mono text-primary font-semibold">
                {formatNumber(item.commission_amount)}
              </TableCell>
              <TableCell>{formatDate(item.enrollment_date)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
